import { CareerCapability } from './transform';
import { careerAccess } from './useCareerAccess';
import { useCareerJourney } from './useCareerJourney';

/**
 * Пропускать ли запрос раздела, закрытого этому зрителю (ТЗ 0069).
 *
 * 🔴 Это САМОЕ ОПАСНОЕ место переезда гейта, и оно не про вёрстку. Раньше
 * не-участника выбрасывал 404 ДО того, как страница успевала что-то заказать.
 * Теперь он остаётся на экране, и если хуки раздела смонтируются, сервер
 * отобьёт их `PermissionDenied` — страница покажет «ошибка загрузки» вместо
 * объяснения «раздел откроется участникам». То есть новая архитектура
 * прочитается как поломка.
 *
 * Красивая заглушка на странице от этого НЕ спасает: заглушка рисуется, а
 * запросы всё равно уходят. Спасает только пропуск в каждом хуке раздела.
 *
 * Запрос маршрута тут не лишний: он смонтирован в `CareerLayout`, кэш общий,
 * и повторного похода в сеть не будет.
 */
export const useCareerCapabilitySkip = (capability: CareerCapability): boolean => {
  const { journey } = useCareerJourney();
  const access = careerAccess(journey, capability);
  // `unknown` (маршрут ещё не приехал) НЕ пропускает запрос: иначе первый кадр
  // каждого захода терял бы данные и экран мигал бы пустотой. Пропускаем ровно
  // то, что сервер точно откажет.
  //
  // ⚠️ Из этого следует, что на ХОЛОДНОМ кэше пропуск не срабатывает вовсе:
  // маршрут и данные раздела монтируются в одном проходе. Поэтому страницы
  // закрытых разделов обязаны ЖДАТЬ ответа маршрута (`useCareerSectionAccess`
  // ниже), а не только показывать заглушку по `locked`: иначе не-участник
  // успевает увидеть полный экран с «уроков пока нет», который затем
  // схлопывается в объяснение — то есть ровно «архитектура прочиталась как
  // поломка».
  return access === 'locked';
};

export interface ICareerSectionAccess {
  /** Раздел закрыт — показываем объяснение с одним призывом. */
  locked: boolean;
  /** Ответ ещё не пришёл — показываем ожидание, а не содержимое раздела. */
  pending: boolean;
}

/** Что показывать на странице раздела: содержимое, объяснение или ожидание. */
export const useCareerSectionAccess = (capability: CareerCapability): ICareerSectionAccess => {
  const { journey } = useCareerJourney();
  const access = careerAccess(journey, capability);
  return { locked: access === 'locked', pending: access === 'unknown' };
};
